<template>
  <div class="inbox-table-container">
    <ve-table
      max-height="calc(100vh - 35rem)"
      :fixed-header="true"
      :columns="columns"
      :table-data="tableData"
    />
    <div v-if="isLoading" class="inboxes-loader">
      <spinner />
      <span>{{
        $t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.LOADING_MESSAGE')
      }}</span>
    </div>
    <empty-state
      v-else-if="!isLoading && !inboxMetrics.length"
      :title="$t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.NO_AGENTS')"
    />
    <div v-if="inboxMetrics.length > 0" class="table-pagination">
      <ve-pagination
        :total="inboxes.length"
        :page-index="pageIndex"
        :page-size="50"
        :page-size-option="[50]"
        @on-page-number-change="onPageNumberChange"
      />
    </div>
  </div>
</template>

<script>
import { VeTable, VePagination } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  name: 'InboxTable',
  components: {
    EmptyState,
    Spinner,
    VeTable,
    VePagination,
  },
  mixins: [rtlMixin],
  props: {
    inboxes: {
      type: Array,
      default: () => [],
    },
    inboxMetrics: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    pageIndex: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    tableData() {
      if (!Array.isArray(this.inboxMetrics)) {
        return [];
      }

      const sortedMetrics = [...this.inboxMetrics].sort(
        (a, b) => (b.metric.urgent || 0) - (a.metric.urgent || 0)
      );

      return sortedMetrics.map(inbox => ({
        inbox: inbox.name,
        thumbnail: inbox.avatar_url,
        open: inbox.metric.open || 0,
        unattended: inbox.metric.unattended || 0,
        none: inbox.metric.none || 0,
        low: inbox.metric.low || 0,
        medium: inbox.metric.medium || 0,
        high: inbox.metric.high || 0,
        urgent: inbox.metric.urgent || 0,
      }));
    },
    columns() {
      return [
        {
          field: 'inbox',
          key: 'inbox',
          title: this.$t(
            'OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.AGENT'
          ),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 25,
          renderBodyCell: ({ row }) => (
            <div class="row-user-block">
              <Thumbnail
                src={row.thumbnail}
                size="32px"
                username={row.inbox}
                status={row.status}
              />
              <div class="user-block">
                <h6 class="title text-truncate">{row.inbox}</h6>
                <span class="sub-title">{row.email}</span>
              </div>
            </div>
          ),
        },
        {
          field: 'open',
          key: 'open',
          title: this.$t(
            'OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.OPEN'
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'none',
          key: 'none',
          renderHeaderCell: () => (
            <span class="priority-undefined">
              {this.$t(
                'OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.NONE'
              )}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'low',
          key: 'low',
          renderHeaderCell: () => (
            <span class="priority-low">
              {this.$t('OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.LOW')}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'medium',
          key: 'medium',
          renderHeaderCell: () => (
            <span class="priority-medium">
              {this.$t(
                'OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.MEDIUM'
              )}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'high',
          key: 'high',
          renderHeaderCell: () => (
            <span class="priority-high">
              {this.$t(
                'OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.HIGH'
              )}
            </span>
          ),
          align: 'center',
          width: 10,
        },
        {
          field: 'urgent',
          key: 'urgent',
          renderHeaderCell: () => (
            <span class="priority-urgent">
              {this.$t(
                'OVERVIEW_REPORTS.INBOX_CONVERSATIONS.TABLE_HEADER.URGENT'
              )}
            </span>
          ),
          align: 'center',
          width: 10,
        },
      ];
    },
  },
  methods: {
    getPriorityClass(priority) {
      switch (priority) {
        case 'urgent':
          return 'priority-urgent';
        case 'high':
          return 'priority-high';
        case 'medium':
          return 'priority-medium';
        case 'low':
          return 'priority-low';
        default:
          return 'priority-undefined';
      }
    },
    onPageNumberChange(pageIndex) {
      this.$emit('page-change', pageIndex);
    },
    getInboxInformation(id) {
      return this.inboxes.find(inbox => inbox.id === Number(id));
    },
  },
};
</script>

<style lang="scss" scoped>
.inbox-table-container {
  display: flex;
  flex-direction: column;
  flex: 1;

  .ve-table {
    &::v-deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &::v-deep .ve-pagination {
    background-color: transparent;
  }

  &::v-deep .ve-pagination-select {
    display: none;
  }

  .row-user-block {
    align-items: center;
    display: flex;
    text-align: left;

    .user-block {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      min-width: 0;
      margin: 0 var(--space-small);

      .title {
        font-size: var(--font-size-small);
        margin: var(--zero);
        line-height: 1.2;
      }
      .sub-title {
        font-size: var(--font-size-mini);
      }
    }
  }

  .table-pagination {
    margin-top: var(--space-normal);
    text-align: right;
  }
}

.inboxes-loader {
  align-items: center;
  display: flex;
  font-size: var(--font-size-default);
  justify-content: center;
  padding: var(--space-large);
}
.priority-urgent {
  background-color: var(--urgent-color);
}
.priority-high {
  background-color: var(--high-color);
}
.priority-medium {
  background-color: var(--medium-color);
}
.priority-low {
  background-color: var(--low-color);
}
.priority-undefined {
  background-color: var(--none-color);
}
.priority-urgent,
.priority-high,
.priority-medium,
.priority-low,
.priority-undefined {
  display: inline-block;
  color: var(--white);
  padding: var(--space-smaller) var(--space-slab);
  border-radius: var(--space-small);
  font-weight: var(--font-weight-bold);
  text-align: center;
}
.inbox-table-container::v-deep {
  .ve-table {
    thead.ve-table-header .ve-table-header-tr .ve-table-header-th {
      font-size: var(--font-size-mini);
      padding: var(--space-small) var(--space-two);
    }
    tbody.ve-table-body .ve-table-body-tr .ve-table-body-td {
      padding: var(--space-slab) var(--space-two);
    }
  }
}
</style>
